/* Avant / Après — Comparaison de KPIs entre deux périodes.
 *
 * Objectif : démontrer la valeur créée par Noam en montrant l'évolution
 * quantitative des indicateurs clés entre deux fenêtres au choix.
 *
 * Presets : Mois vs mois précédent · Trimestre vs trimestre · Semaine vs
 * semaine · Depuis onboarding (custom). L'utilisateur peut aussi saisir
 * les deux périodes manuellement.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, ArrowRight, TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";

function toISO(d) {
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
}

function startOfMonth(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfMonth(offset = 0) {
  const d = startOfMonth(offset + 1);
  d.setDate(d.getDate() - 1);
  return d;
}
function startOfWeek(offset = 0) {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - day + offset * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfWeek(offset = 0) {
  const d = startOfWeek(offset);
  d.setDate(d.getDate() + 6);
  return d;
}
function startOfQuarter(offset = 0) {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3);
  d.setMonth((q + offset) * 3);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfQuarter(offset = 0) {
  const d = startOfQuarter(offset + 1);
  d.setDate(d.getDate() - 1);
  return d;
}

const PRESETS = [
  {
    key: "month",
    label: "Mois précédent → mois en cours",
    build: () => ({
      a: [startOfMonth(-1), endOfMonth(-1)],
      b: [startOfMonth(0), endOfMonth(0)],
    }),
  },
  {
    key: "week",
    label: "Semaine précédente → semaine en cours",
    build: () => ({
      a: [startOfWeek(-1), endOfWeek(-1)],
      b: [startOfWeek(0), endOfWeek(0)],
    }),
  },
  {
    key: "quarter",
    label: "Trimestre précédent → trimestre en cours",
    build: () => ({
      a: [startOfQuarter(-1), endOfQuarter(-1)],
      b: [startOfQuarter(0), endOfQuarter(0)],
    }),
  },
  {
    key: "quarter_yoy",
    label: "Trimestre N-1 → trimestre N (année sur année)",
    build: () => {
      const [a1, a2] = [startOfQuarter(-4), endOfQuarter(-4)];
      const [b1, b2] = [startOfQuarter(0), endOfQuarter(0)];
      return { a: [a1, a2], b: [b1, b2] };
    },
  },
];

function DeltaBadge({ pct }) {
  const isPos = pct > 0;
  const isNeg = pct < 0;
  const cls = isPos
    ? "text-[#1D9E75] bg-[#1D9E75]/10 border-[#1D9E75]/30"
    : isNeg
      ? "text-[#E24B4A] bg-[#E24B4A]/10 border-[#E24B4A]/30"
      : "text-zinc-400 bg-white/[0.04] border-white/10";
  const Icon = isPos ? TrendingUp : isNeg ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
      <Icon className="w-3 h-3" />
      {isPos ? "+" : ""}{pct}%
    </span>
  );
}

function IndicatorRow({ ind }) {
  const maxVal = Math.max(ind.value_a, ind.value_b, 1);
  const pctA = (ind.value_a / maxVal) * 100;
  const pctB = (ind.value_b / maxVal) * 100;
  return (
    <div className="noam-card p-4 space-y-3" data-testid={`ava-indicator-${ind.key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">{ind.label}</div>
          <div className="text-xl font-bold text-white mt-0.5" data-testid={`ava-value-b-${ind.key}`}>
            {ind.value_b}
            <span className="text-[11px] text-zinc-500 font-normal ml-1.5">après</span>
          </div>
        </div>
        <DeltaBadge pct={ind.delta_pct} />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[9px] text-zinc-500 w-10 flex-shrink-0">avant</span>
          <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pctA}%`, background: `${ind.color}80` }} />
          </div>
          <span className="text-[10px] text-zinc-400 w-8 text-right flex-shrink-0 tabular-nums">{ind.value_a}</span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[9px] text-zinc-500 w-10 flex-shrink-0">après</span>
          <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pctB}%`, background: ind.color }} />
          </div>
          <span className="text-[10px] text-white w-8 text-right flex-shrink-0 tabular-nums font-semibold">{ind.value_b}</span>
        </div>
      </div>
    </div>
  );
}

export default function AvantApres() {
  const [preset, setPreset] = useState("month");
  const [dates, setDates] = useState(() => {
    const p = PRESETS[0].build();
    return {
      a_start: toISO(p.a[0]), a_end: toISO(p.a[1]),
      b_start: toISO(p.b[0]), b_end: toISO(p.b[1]),
    };
  });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const p = PRESETS.find((x) => x.key === key)?.build();
    if (p) {
      setDates({
        a_start: toISO(p.a[0]), a_end: toISO(p.a[1]),
        b_start: toISO(p.b[0]), b_end: toISO(p.b[1]),
      });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/avant-apres", { params: dates });
      setData(r.data);
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dates.a_start, dates.a_end, dates.b_start, dates.b_end]);

  const totals = data?.totals || { value_a: 0, value_b: 0, delta_abs: 0, delta_pct: 0 };
  const topMovers = useMemo(() => {
    if (!data?.indicators) return [];
    return [...data.indicators]
      .filter((i) => i.value_a > 0 || i.value_b > 0)
      .sort((a, b) => Math.abs(b.delta_pct) - Math.abs(a.delta_pct))
      .slice(0, 3);
  }, [data]);

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Avant / Après</div>
          <h1 className="noam-display text-2xl mt-1">Ce que Noam a fait bouger.</h1>
          <p className="text-xs text-zinc-400 mt-1 max-w-xl">
            Choisissez deux périodes — Noam compare le patrimoine décisionnel créé (signaux détectés, risques cartographiés, opportunités qualifiées, plans d&apos;action lancés…) et vous montre où l&apos;organisation a réellement avancé.
          </p>
        </div>
      </div>

      {/* Sélecteur de périodes */}
      <div className="noam-card p-4 space-y-3" data-testid="ava-selector">
        <div className="flex items-center gap-2 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p.key)}
              className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded border transition-colors ${
                preset === p.key
                  ? "border-[#EF9F27] text-[#EF9F27] bg-[#EF9F27]/10"
                  : "border-white/10 text-zinc-400 hover:text-white hover:border-white/25"
              }`}
              data-testid={`ava-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => setPreset("custom")}
            className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded border transition-colors ${
              preset === "custom"
                ? "border-[#378ADD] text-[#378ADD] bg-[#378ADD]/10"
                : "border-white/10 text-zinc-400 hover:text-white hover:border-white/25"
            }`}
            data-testid="ava-preset-custom"
          >
            Sur mesure
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: "a", label: "Avant", start: "a_start", end: "a_end", color: "#71717a" },
            { key: "b", label: "Après", start: "b_start", end: "b_end", color: "#EF9F27" },
          ].map((seg) => (
            <div key={seg.key} className="rounded border border-white/10 p-3 space-y-2" style={{ borderColor: `${seg.color}30` }}>
              <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: seg.color }}>{seg.label}</div>
              <div className="flex items-center gap-1.5 min-w-0">
                <input
                  type="date"
                  value={dates[seg.start]}
                  onChange={(e) => { setPreset("custom"); setDates((d) => ({ ...d, [seg.start]: e.target.value })); }}
                  className="text-[11px] bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-white focus:outline-none focus:border-white/30 min-w-0 flex-1"
                  data-testid={`ava-${seg.key}-start`}
                />
                <ArrowRight className="w-3 h-3 text-zinc-500 flex-shrink-0" />
                <input
                  type="date"
                  value={dates[seg.end]}
                  onChange={(e) => { setPreset("custom"); setDates((d) => ({ ...d, [seg.end]: e.target.value })); }}
                  className="text-[11px] bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-white focus:outline-none focus:border-white/30 min-w-0 flex-1"
                  data-testid={`ava-${seg.key}-end`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Total + verdict */}
      {loading ? (
        <div className="text-[11px] text-zinc-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Calcul de l&apos;écart en cours…
        </div>
      ) : data ? (
        <>
          <div className="noam-card p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="ava-summary">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Patrimoine décisionnel créé</div>
              <div className="flex items-baseline gap-3 mt-1 flex-wrap">
                <span className="text-3xl font-bold text-white tabular-nums" data-testid="ava-total-b">{totals.value_b}</span>
                <span className="text-zinc-500 text-xs">nouveaux objets « après »</span>
                <span className="text-zinc-600">·</span>
                <span className="text-sm text-zinc-400 tabular-nums" data-testid="ava-total-a">{totals.value_a}</span>
                <span className="text-zinc-500 text-xs">« avant »</span>
              </div>
              {topMovers.length > 0 && (
                <p className="text-[11px] text-zinc-400 mt-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-[#EF9F27] flex-shrink-0" />
                  Mouvement dominant :
                  {topMovers.map((m, i) => (
                    <span key={m.key} className="ml-1">
                      <span style={{ color: m.color }}>{m.label}</span>
                      <span className={m.delta_pct >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>
                        {" "}({m.delta_pct >= 0 ? "+" : ""}{m.delta_pct}%)
                      </span>
                      {i < topMovers.length - 1 ? "," : ""}
                    </span>
                  ))}
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              <DeltaBadge pct={totals.delta_pct} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ava-indicators">
            {(data.indicators || []).map((ind) => (
              <IndicatorRow key={ind.key} ind={ind} />
            ))}
          </div>
        </>
      ) : (
        <div className="noam-card p-8 text-center text-zinc-400 text-xs">Aucune donnée sur la période sélectionnée.</div>
      )}
    </>
  );
}
